<template>
  <modal id="clustersForm"
         v-model="visible"
         :title="id ? $t('a-clustersForm:updateCluster') : $t('a-clustersForm:createCluster')"
         :callback="id ? updateCluster : createCluster"
         size="lg"
         @modal:cancel="$emit('cancel')"
  >
    <small slot="modal-header-right" class="text-muted font-italic">{{ $t('a-clustersForm:required') | capitalize }}</small>

    <div slot="modal-body">
      <div class="form-row">
        <div v-if="isRole.superAdmin" class="form-group col-lg-6">
          <label for="customer" class="control-label">{{ $t('a-clustersForm:customer') | capitalize }} *</label>
          <select id="customer"
                  v-model="customer"
                  v-validate="{required: isRole.superAdmin}"
                  :data-vv-as="$t('a-clustersForm:customer')"
                  :class="{ 'is-invalid': errors.has('customer') }"
                  name="customer"
                  class="form-control"
          >
            <option v-for="c in $store.getters.customers" :key="c.id" :value="c" :data-customer-id="c.id">{{ c.name }}</option>
          </select>
          <div class="invalid-feedback">{{ errors.first('customer') }}</div>
        </div>
      </div>

      <div class="form-row">
        <div class="form-group col-lg-6">
          <label for="name" class="control-label">{{ $t('a-clustersForm:name') | capitalize }} *</label>
          <input id="name"
                 v-model="name"
                 v-validate="'required|text'"
                 :data-vv-as="$t('a-clustersForm:name')"
                 :class="{ 'is-invalid': errors.has('name') }"
                 type="text"
                 name="name"
                 class="form-control"
          >
          <div class="invalid-feedback">{{ errors.first('name') }}</div>
        </div>

        <div class="form-group col-lg-5 offset-lg-1">
          <label for="payloadType" class="control-label d-block">{{ $t('a-clustersForm:payloadType') | capitalize }} *</label>
          <input id="payloadTypeHex"
                 v-model="payloadType"
                 v-validate="'required'"
                 :data-vv-as="$t('a-clustersForm:payloadType')"
                 :class="{ 'is-invalid': errors.has('payloadType') }"
                 type="radio"
                 value="HEX"
                 name="payloadType"
          >
          <label for="payloadTypeHex" class="control-label">{{ $t('a-clustersForm:payloadTypeHexa') | capitalize }}</label>

          <input id="payloadTypeB64"
                 v-model="payloadType"
                 :class="{ 'is-invalid': errors.has('payloadType') }"
                 type="radio"
                 value="B64"
                 name="payloadType"
          >
          <label for="payloadTypeB64" class="control-label">{{ $t('a-clustersForm:payloadTypeB64') | capitalize }}</label>

          <div class="invalid-feedback">{{ errors.first('payloadType') }}</div>
        </div>
      </div>

      <h3 class="mt-3">{{ $t('a-clustersForm:pushTitle') | capitalize }}</h3>

      <div class="form-row">
        <div class="form-group col-lg-12">
          <input id="pushEnable"
                 v-model="pushEnabled"
                 v-validate="'required'"
                 :data-vv-as="$t('a-clustersForm:pushEnable')"
                 :class="{ 'is-invalid': errors.has('pushEnabled') }"
                 :value="true"
                 type="radio"
                 name="pushEnabled"
          >
          <label for="pushEnable" class="control-label">{{ $t('a-clustersForm:pushEnable') | capitalize }}</label>

          <input id="pushDisable"
                 v-model="pushEnabled"
                 :class="{ 'is-invalid': errors.has('pushEnabled') }"
                 :value="false"
                 type="radio"
                 name="pushEnabled"
          >
          <label for="pushDisable" class="control-label">{{ $t('a-clustersForm:pushDisable') | capitalize }}</label>

          <div class="invalid-feedback">{{ errors.first('pushEnabled') }}</div>
        </div>
      </div>

      <div class="form-row">
        <div v-if="pushEnabled" class="form-group col-lg-6">
          <label for="pushConfiguration" class="control-label">{{ $t('a-clustersForm:pushConfiguration') | capitalize }}<span v-if="pushEnabled"> *</span></label>
          <select id="pushConfiguration"
                  v-model="pushConfiguration"
                  v-validate="{required: pushEnabled}"
                  :data-vv-as="$t('a-clustersForm:pushConfiguration')"
                  :class="{ 'is-invalid': errors.has('pushConfiguration') }"
                  name="pushConfiguration"
                  class="form-control"
          >
            <option v-for="pushConf in pushConfigurations" :key="pushConf.id" :value="pushConf" :data-push-configuration-id="pushConf.id">{{ pushConf.name }}</option>
          </select>
          <div class="invalid-feedback">{{ errors.first('pushConfiguration') }}</div>
        </div>
      </div>

      <h3 v-show="isGeolocationAuthorized" class="mt-3">{{ $t('a-clustersForm:geolocation') | capitalize }}</h3>

      <div class="form-row">
        <div v-show="isGeolocationAuthorized" class="form-group col-lg-12">
          <input id="geolocEnabled"
                 v-model="geolocEnabled"
                 type="checkbox"
                 name="geolocEnabled"
          >
          <label for="geolocEnabled" />
          <label for="geolocEnabled" class="control-label">{{ $t('a-clustersForm:geolocationEnable') | capitalize }}</label>
        </div>
      </div>
    </div>
  </modal>
</template>

<script>
import { isEmpty, pick, find } from 'lodash-es'
import to from '@/modules/await-to-js'
import eventBus from '@/modules/eventBus'

export default {
  model: {
    prop: 'show',
    event: 'change'
  },

  props: {
    // Display or hide modal
    // To be used as v-model
    show: {
      type: Boolean,
      required: true
    },

    clusterToEdit: {
      type: Object,
      default: null
    }
  },

  data () {
    return {
      // Display or hide b-modal
      // It cannot change our props, so we need another variable
      visible: false,
      // List
      pushConfigurations: [],
      // Form
      id: '',
      name: '',
      customer: null,
      pushEnabled: true,
      pushConfiguration: null,
      payloadType: 'HEX',
      geolocEnabled: false
    }
  },

  computed: {
    // Is geolocation enable for customer?
    isGeolocationAuthorized () {
      return this.customer &&
        this.customer.geolocationAuthorized &&
        (
          !this.customer.geolocationExpirationDate ||
          (this.customer.geolocationExpirationDate && this.customer.geolocationExpirationDate > Date.now())
        )
    }
  },

  watch: {
    // Synchronize show and visible
    // Use change event to update v-model
    show () {
      this.reset()
      this.visible = this.show

      if (this.show) {
        // Get customers
        if (this.isRole.superAdmin) {
          this.$store.dispatch('getCustomers').then(() => {
            if (this.id) this.customer = find(this.$store.getters.customers, customer => customer.id === this.clusterToEdit.customer.id)
          })
        } else {
          this.customer = this.$store.getters.authenticatedUserCustomer
        }
      }
    },
    visible () {
      this.$emit('change', this.visible)
    },

    customer () {
      // Reset geolocation when customer change and have not geolocation disabled
      if (!this.isGeolocationAuthorized) this.geolocEnabled = false

      if (!this.customer) return
      // Get push configurations
      const customerSearch = this.$utils.addSearch([], 'eq', this.customer.id, 'customer.id')
      const params = this.$utils.getParams({ sort: '+name', search: JSON.stringify(customerSearch[0]) })
      this.$api.getPushConfigurations(this.$store.getters.application, params).then(response => {
        this.pushConfigurations = response.data.list
        if (this.id && this.pushEnabled) this.pushConfiguration = find(this.pushConfigurations, pushConf => pushConf.id === this.clusterToEdit.pushConfiguration.id)
      })
    },

    clusterToEdit () {
      if (!isEmpty(this.clusterToEdit)) {
        this.id = this.clusterToEdit.id
        this.name = this.clusterToEdit.name
        this.pushEnabled = this.clusterToEdit.pushEnabled
        this.payloadType = this.clusterToEdit.hexa ? 'HEX' : 'B64'
        this.geolocEnabled = this.clusterToEdit.geolocEnabled
      }
    }
  },

  methods: {
    async reset () {
      this.id = ''
      this.name = ''
      this.customer = null
      this.pushEnabled = true
      this.pushConfiguration = null
      this.payloadType = 'HEX'
      this.geolocEnabled = false
      this.pushConfigurations = []

      // Reset form validation
      await this.$nextTick()
      this.$validator.reset()
    },

    async createCluster () {
      const valid = await this.$validator.validateAll()
      if (!valid) return

      const fields = [ 'name', 'pushEnabled', 'geolocEnabled' ]
      const cluster = pick(this, fields)
      if (this.pushEnabled) cluster.pushConfiguration = { id: this.pushConfiguration.id }
      cluster.customer = { id: this.customer.id }
      cluster.hexa = this.payloadType === 'HEX'

      let error
      [error] = await to(this.$api.createCluster(this.$store.getters.application, cluster))
      if (error) return

      this.visible = false
      // Success
      eventBus.$emit('message-success', this.$t('a-clustersForm:createSuccess'))
      // Reset form
      this.reset()
      // Refresh list
      this.$emit('success')
    },

    async updateCluster () {
      const valid = await this.$validator.validateAll()
      if (!valid) return

      const fields = [ 'name', 'pushEnabled', 'geolocEnabled' ]
      const cluster = pick(this, fields)
      if (this.pushEnabled) cluster.pushConfiguration = { id: this.pushConfiguration.id }
      if (this.isRole.superAdmin) cluster.customer = { id: this.customer.id }
      cluster.hexa = this.payloadType === 'HEX'

      let error
      [error] = await to(this.$api.updateCluster(this.clusterToEdit, cluster))
      if (error) return

      this.visible = false
      // Update selection
      this.$store.commit('updateClusterInSelection', { id: this.id, name: this.name })
      // Success
      eventBus.$emit('message-success', this.$t('a-clustersForm:editSuccess'))
      // Reset form
      this.reset()
      // Refresh list
      this.$emit('success')
    }
  }
}
</script>
